// Single source of truth for the settings dialog's sections.
//
// Order, labels, icons, and visibility all live here so the nav, the panel,
// the deep-link parser, and the tests cannot disagree about what exists. Adding
// a section is one entry; there is no second list to keep in sync.
//
// NOT every user-menu item belongs here. Cookie settings stays a dropdown item
// because the preferences dialog is owned and globally mounted by
// ConsentProvider, and Admin console stays a dropdown item because it is a
// protected route, not a panel body. Duplicating either one inside the shell
// would fork behaviour that already has exactly one implementation.

import type { LucideIcon } from 'lucide-react'

/**
 * Known section ids. This is the accepted set for the `?settings=` deep link —
 * anything else falls back to `profile` rather than erroring, so a stale
 * bookmark or a link to a not-yet-enabled section still opens something useful.
 */
export type SettingsSectionId =
  | 'profile'
  | 'general'
  | 'personalization'
  | 'teams'
  | 'usage-credits'

export const SETTINGS_SECTION_IDS: readonly SettingsSectionId[] = [
  'profile',
  'general',
  'personalization',
  'teams',
  'usage-credits',
]

export const DEFAULT_SETTINGS_SECTION: SettingsSectionId = 'profile'

export function isSettingsSectionId(value: string | null | undefined): value is SettingsSectionId {
  // `some` rather than `includes`: comparing a plain string against a
  // readonly SettingsSectionId[] would otherwise need a cast, and casts are
  // rejected by the forbidden-TS-syntax lint.
  return value != null && SETTINGS_SECTION_IDS.some((id) => id === value)
}

/** What a section needs to decide whether it should appear at all. */
export interface SettingsSectionVisibilityContext {
  /** True for @meta.com (or the local debug bypass). Gates billing. */
  canSeeBilling: boolean
  /** `billing_sandbox_enabled`. Independent of, and ANDed with, the audience gate. */
  billingSandboxEnabled: boolean
  /** Owner/Admin access to the selected team's financial data. */
  canViewCredits: boolean
  /** The selected team uses current-period postpaid usage instead of a wallet. */
  isPostpaid: boolean
  /**
   * `teams_enabled` (RFC-1371). The sole gate on the Teams section — with it
   * off the section does not exist, the nav does not list it, and the
   * `?settings=teams` deep link falls back to Profile like any other unknown
   * id. There is no audience check ANDed with it: unlike billing, teams is not
   * scoped to an internal audience, only to a rollout flag.
   */
  teamsEnabled: boolean
}

export interface SettingsSectionDefinition {
  id: SettingsSectionId
  /** Key into the `settings` i18n namespace. */
  labelKey: string
  postpaidLabelKey?: string
  /** lucide-react icon, rendered at 20px by the nav. */
  Icon: LucideIcon
  /** Omit the section entirely when false. */
  isVisible: (ctx: SettingsSectionVisibilityContext) => boolean
}
